<script setup lang="ts">
  import type { HTMLAttributes } from "vue";
  import { computed, ref, nextTick, watch } from "vue";
  import { CalendarDate, type DateValue } from "@internationalized/date";
  import { CalendarIcon } from "lucide-vue-next";
  import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
  import { Button } from "@/components/ui/button";
  import { Calendar } from "@/components/ui/calendar";
  import { ScrollArea } from "@/components/ui/scroll-area";
  import { cn } from "@/lib/utils";

  const props = withDefaults(
    defineProps<{
      modelValue?: string | Date | null;
      placeholder?: string;
      disabled?: boolean;
      clearable?: boolean;
      timePicker?: boolean;
      class?: HTMLAttributes["class"];
    }>(),
    {
      modelValue: null,
      placeholder: "Pick a date",
      disabled: false,
      clearable: true,
      timePicker: false,
      class: undefined,
    },
  );

  const emit = defineEmits<{
    "update:modelValue": [value: string | null];
  }>();

  const isOpen = ref(false);
  const timeListRef = ref<InstanceType<typeof ScrollArea> | null>(null);

  const parsedDate = computed(() => {
    if (!props.modelValue) return null;
    const d = new Date(props.modelValue);
    return isNaN(d.getTime()) ? null : d;
  });

  const calendarValue = computed<DateValue | undefined>({
    get() {
      if (!parsedDate.value) return undefined;
      const d = parsedDate.value;
      return new CalendarDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
    },
    set(val: DateValue | undefined) {
      if (!val) {
        emit("update:modelValue", null);
        return;
      }
      const existing = parsedDate.value;
      const hours = existing ? existing.getHours() : 0;
      const minutes = existing ? existing.getMinutes() : 0;
      const d = new Date(val.year, val.month - 1, val.day, hours, minutes);
      emit("update:modelValue", d.toISOString());
      if (!props.timePicker) {
        isOpen.value = false;
      }
    },
  });

  const timeSlots = computed(() => {
    const slots = [];
    for (let i = 0; i < 96; i++) {
      const hours = Math.floor(i / 4);
      const minutes = (i % 4) * 15;
      slots.push({
        label: `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`,
        hours,
        minutes,
      });
    }
    return slots;
  });

  const selectedTimeLabel = computed(() => {
    if (!parsedDate.value) return null;
    const h = String(parsedDate.value.getHours()).padStart(2, "0");
    const m = String(parsedDate.value.getMinutes()).padStart(2, "0");
    return `${h}:${m}`;
  });

  const selectTime = (hours: number, minutes: number) => {
    const existing = parsedDate.value || new Date();
    const d = new Date(existing.getFullYear(), existing.getMonth(), existing.getDate(), hours, minutes);
    emit("update:modelValue", d.toISOString());
    isOpen.value = false;
  };

  const scrollToSelectedTime = () => {
    if (!parsedDate.value) return;
    nextTick(() => {
      const el = document.querySelector("[data-time-selected='true']");
      el?.scrollIntoView({ block: "center" });
    });
  };

  watch(isOpen, (val) => {
    if (val && props.timePicker) scrollToSelectedTime();
  });

  const displayValue = computed(() => {
    if (!parsedDate.value) return "";
    const d = parsedDate.value;
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    if (props.timePicker) {
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
    return `${day}/${month}/${year}`;
  });
</script>

<template>
  <Popover v-model:open="isOpen">
    <PopoverTrigger as-child>
      <Button
        variant="outline"
        :disabled="disabled"
        :class="cn('w-full justify-start text-left font-normal', !modelValue && 'text-muted-foreground', props.class)">
        <CalendarIcon class="mr-2 h-4 w-4" />
        {{ displayValue || placeholder }}
      </Button>
    </PopoverTrigger>
    <PopoverContent
      class="w-auto p-0"
      align="start">
      <div :class="timePicker ? 'flex items-start' : ''">
        <Calendar
          v-model="calendarValue"
          fixed-weeks />
        <ScrollArea
          v-if="timePicker"
          ref="timeListRef"
          class="h-[18rem] w-[120px] border-l">
          <div class="p-2">
            <Button
              v-for="slot in timeSlots"
              :key="slot.label"
              variant="ghost"
              size="sm"
              :data-time-selected="selectedTimeLabel === slot.label"
              :class="cn(
                'w-full justify-center font-mono',
                selectedTimeLabel === slot.label && 'bg-accent font-semibold',
              )"
              @click="selectTime(slot.hours, slot.minutes)">
              {{ slot.label }}
            </Button>
          </div>
        </ScrollArea>
      </div>
    </PopoverContent>
  </Popover>
</template>
